import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export interface MarketScope {
  id: string;
  name: string;
  emoji: string;
  description: string;
}

interface MarketScopeSelectProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
}

export const MarketScopeSelect: React.FC<MarketScopeSelectProps> = ({
  value,
  onChange,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();
  
  const marketScopes: MarketScope[] = [
    {
      id: 'local',
      name: t('brands.edit.marketingStrategy.marketScope.local', 'Local'),
      emoji: '🏪',
      description: t('brands.edit.marketingStrategy.marketScope.localDescription', 'Physical businesses serving specific cities/regions')
    },
    {
      id: 'regional',
      name: t('brands.edit.marketingStrategy.marketScope.regional', 'Regional'),
      emoji: '🗺️',
      description: t('brands.edit.marketingStrategy.marketScope.regionalDescription', 'Businesses serving multiple cities/regions within a country')
    },
    {
      id: 'national',
      name: t('brands.edit.marketingStrategy.marketScope.national', 'National'),
      emoji: '🏛️',
      description: t('brands.edit.marketingStrategy.marketScope.nationalDescription', 'Businesses serving an entire country')
    },
    {
      id: 'international',
      name: t('brands.edit.marketingStrategy.marketScope.international', 'International'),
      emoji: '🌍',
      description: t('brands.edit.marketingStrategy.marketScope.internationalDescription', 'Businesses serving multiple countries but not globally')
    },
    {
      id: 'global',
      name: t('brands.edit.marketingStrategy.marketScope.global', 'Global'),
      emoji: '🌐',
      description: t('brands.edit.marketingStrategy.marketScope.globalDescription', 'Businesses that can serve customers worldwide')
    }
  ];
  
  const selectedScope = marketScopes.find(scope => scope.id === value) || marketScopes[0];

  return (
    <div className="relative">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className={`w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg bg-white text-left ${className}`}
      >
        <span className="flex items-center gap-2">
          <span className="text-lg">{selectedScope.emoji}</span>
          <span className="text-gray-700">{selectedScope.name}</span>
        </span>
        <ChevronDown className="w-4 h-4 text-gray-500" />
      </button>

      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-10"
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(false);
            }}
          />
          <div className="absolute left-0 right-0 mt-2 rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 z-20">
            <div className="py-1">
              {marketScopes.map(scope => (
                <button
                  type="button"
                  key={scope.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange(scope.id);
                    setIsOpen(false);
                  }}
                  className={`w-full text-left px-4 py-2 ${
                    scope.id === value
                      ? 'bg-primary-50 text-primary-600'
                      : 'text-gray-700 hover:text-primary-600'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{scope.emoji}</span>
                    <div>
                      <div className="font-medium">{scope.name}</div>
                      <div className="text-xs text-gray-500">{scope.description}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}; 